import { useEffect, useState } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Button, Switch, FormControlLabel, Alert, MenuItem, Paper, Chip,
} from '@mui/material';
import { getTiers, upsertTier } from '../../api/tierApi';
import { getMarkupShare, saveMarkupShare } from '../../api/generalSettingsApi';

const PP = "'Poppins', sans-serif";
const COLOR_OPTS = ['slate', 'bronze', 'silver', 'gold', 'diamond'];

const BADGE_BG = {
  slate: '#F1F5F9', bronze: '#FEF3C7', silver: '#E5E7EB', gold: '#FEF3C7', diamond: '#DBEAFE',
};
const BADGE_FG = {
  slate: '#475569', bronze: '#92400E', silver: '#374151', gold: '#B45309', diamond: '#1D4ED8',
};

/**
 * Mark-up Commission (MUC) — the share of a price mark-up paid to the agent who sold
 * it. A mark-up is a negative agent discount: the customer paid above the list price,
 * and that difference is handed over whole rather than percentaged, so it never moves
 * PSC, DOC or TOC. 100% means the agent keeps all of it; the remainder stays with the
 * Company. An agent with their own MUC % on their user record overrides this, and the
 * list below names everyone who does so nobody is silently off the standard number.
 * (user 2026-11-02: "MUC need to have a % to webbey ... now we set 100% to agent")
 */
function MarkupShareCard() {
  const [fee, setFee] = useState('0');
  const [overrides, setOverrides] = useState([]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    getMarkupShare()
      .then(r => {
        setFee(String(r.data?.feePercent ?? 0));
        setOverrides(r.data?.overrides || []);
      })
      .catch(() => { });
  }, []);

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const res = await saveMarkupShare({ feePercent: Number(fee) || 0 });
      setFee(String(res.data?.feePercent ?? fee));
      setMsg({ type: 'success', text: 'Saved. New sales use this share; commissions already computed are unchanged.' });
    } catch (e) {
      setMsg({ type: 'error', text: 'Save failed: ' + (typeof e.response?.data === 'string' ? e.response.data : e.message) });
    } finally { setSaving(false); }
  };

  const feePct = Math.min(100, Math.max(0, Number(fee) || 0));

  return (
    <Paper sx={{ borderRadius: '12px', p: 2.5, mb: 3 }}>
      <Typography sx={{ fontFamily: PP, fontWeight: 700, fontSize: '0.95rem', mb: 0.5 }}>
        Mark-up Commission (MUC)
      </Typography>
      <Typography sx={{ fontFamily: PP, color: '#64748B', fontSize: '0.82rem', mb: 2 }}>
        When an agent sells above the list price, the difference is paid as its own commission
        line. This is the share of that mark-up the agent receives — the rest stays with the
        Company. It does not change PSC, DOC or TOC, which are still calculated on the list price.
        A co-broking sale splits the mark-up equally between the two agents first.
      </Typography>
      {msg && <Alert severity={msg.type} sx={{ mb: 2 }}>{msg.text}</Alert>}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        {/* ONE BOX. This is a different fee from the EPF/SOCSO processing fee on the slip
            — that one is a per-agent toggle on the whole slip gross; this one is charged
            on the mark-up alone and applies to everybody. Blank reads as 0, which is the
            safe end: the agent keeps the whole mark-up, exactly as today. */}
        <TextField
          label="MUC processing fee %" type="number" size="small" value={fee}
          onChange={e => setFee(e.target.value)}
          helperText="What the Company keeps. 0 = the agent keeps the whole mark-up."
          inputProps={{ min: 0, max: 100, step: 1 }} sx={{ width: 300 }}
        />
        <Chip
          label={`Agent ${(100 - feePct).toFixed(1)}% · Company ${feePct.toFixed(1)}%`}
          sx={{ fontFamily: PP, fontWeight: 600, bgcolor: '#EFF6FF', color: '#0F62CC' }}
        />
        <Button variant="contained" onClick={save} disabled={saving} sx={{ textTransform: 'none' }}>
          {saving ? 'Saving…' : 'Save MUC Share'}
        </Button>
      </Box>
      {overrides.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ fontFamily: PP, fontSize: '0.78rem', color: '#64748B', mb: 0.5 }}>
            Agents on their own fee (set on the user record, overrides the number above):
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            {overrides.map(o => (
              <Chip key={o.userId} size="small" label={`${o.name} — ${o.percent}%`} sx={{ fontFamily: PP }} />
            ))}
          </Box>
        </Box>
      )}
    </Paper>
  );
}

export default function TierSettings() {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);

  const load = () =>
    getTiers().then(r => setRows(r.data || [])).catch(e => setError(e.response?.data ?? e.message));

  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({
    tierId: null,
    name: '',
    sortOrder: rows.length + 1,
    revenueThreshold: 0,
    pscPercent: 5,
    docPercent: 1,
    tocPercent: 5,
    badgeColor: 'slate',
    isActive: true,
  });

  const save = async () => {
    setError('');
    try {
      await upsertTier(editing);
      setEditing(null);
      load();
    } catch (e) {
      setError(e.response?.data ?? e.message);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h6" sx={{ fontFamily: PP, fontWeight: 700, mb: 0.5 }}>
        Tier Settings
      </Typography>
      <Typography sx={{ fontFamily: PP, color: '#64748B', fontSize: '0.85rem', mb: 2 }}>
        Tier names are used as the agent's professional title (e.g. on namecards). PSC% is the rate granted on promotion.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{String(error)}</Alert>}
      <MarkupShareCard />
      <Button variant="contained" onClick={startNew} sx={{ mb: 2, textTransform: 'none' }}>
        + New Tier
      </Button>
      <Paper sx={{ borderRadius: '12px', overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Order</TableCell>
              <TableCell>Title (Tier Name)</TableCell>
              <TableCell>Revenue Threshold (RM)</TableCell>
              <TableCell>PSC %</TableCell>
              <TableCell>DOC %</TableCell>
              <TableCell>TOC %</TableCell>
              <TableCell>Badge</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>—</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(t => (
              <TableRow key={t.tierId} hover>
                <TableCell>{t.sortOrder}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  <Chip
                    label={t.name}
                    size="small"
                    sx={{
                      backgroundColor: BADGE_BG[t.badgeColor] ?? BADGE_BG.slate,
                      color: BADGE_FG[t.badgeColor] ?? BADGE_FG.slate,
                      fontWeight: 700,
                      fontFamily: PP,
                    }}
                  />
                </TableCell>
                <TableCell>{Number(t.revenueThreshold).toLocaleString()}</TableCell>
                <TableCell>{t.pscPercent}%</TableCell>
                <TableCell>{t.docPercent}%</TableCell>
                <TableCell>{t.tocPercent}%</TableCell>
                <TableCell>{t.badgeColor}</TableCell>
                <TableCell>{t.isActive ? '✓' : '—'}</TableCell>
                <TableCell>
                  <Button size="small" onClick={() => setEditing({ ...t })}>Edit</Button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} sx={{ color: '#64748B', textAlign: 'center', py: 3 }}>
                  No tiers configured.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {editing && (
        <Paper sx={{ mt: 3, p: 2, borderRadius: '12px', border: '1px solid #E2E8F0' }}>
          <Typography sx={{ fontFamily: PP, fontWeight: 600, mb: 2 }}>
            {editing.tierId ? `Edit "${editing.name}"` : 'New Tier'}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
            <TextField
              label="Title (Tier Name)"
              size="small"
              value={editing.name}
              onChange={e => setEditing({ ...editing, name: e.target.value })}
              helperText="e.g. Sales Executives / Senior Sales / Sales Lead"
            />
            <TextField
              label="Sort order"
              size="small"
              type="number"
              value={editing.sortOrder}
              onChange={e => setEditing({ ...editing, sortOrder: Number(e.target.value) })}
            />
            <TextField
              label="Revenue Threshold (RM)"
              size="small"
              type="number"
              value={editing.revenueThreshold}
              onChange={e => setEditing({ ...editing, revenueThreshold: Number(e.target.value) })}
              helperText="FinalPrice only since 1 May 2026; cobroke = half-credit"
            />
            <TextField
              label="PSC %"
              size="small"
              type="number"
              value={editing.pscPercent}
              onChange={e => setEditing({ ...editing, pscPercent: Number(e.target.value) })}
            />
            <TextField
              label="DOC %"
              size="small"
              type="number"
              value={editing.docPercent ?? 0}
              onChange={e => setEditing({ ...editing, docPercent: Number(e.target.value) })}
            />
            <TextField
              label="TOC %"
              size="small"
              type="number"
              value={editing.tocPercent ?? 0}
              onChange={e => setEditing({ ...editing, tocPercent: Number(e.target.value) })}
            />
            <TextField
              select
              label="Badge color"
              size="small"
              value={editing.badgeColor}
              onChange={e => setEditing({ ...editing, badgeColor: e.target.value })}
            >
              {COLOR_OPTS.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
            <FormControlLabel
              control={<Switch checked={editing.isActive} onChange={e => setEditing({ ...editing, isActive: e.target.checked })} />}
              label="Active"
            />
          </Box>
          <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button onClick={() => setEditing(null)}>Cancel</Button>
            <Button variant="contained" onClick={save} disabled={!editing.name?.trim()}>Save</Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
}
